import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { MENU_ITEM_ATTR, useMenuHighlight } from "./menu-list.js";

// Exported for ui/combobox, which must look exactly like a Select.
// The data-[state=open] half is not decoration: Radix moves focus into the
// portalled panel when the menu opens, so :focus stops matching the trigger and
// BOTH the brand border and the ring fall off exactly while the menu is open --
// the trigger sat there with a plain dark border as if nothing were active.
// data-state is the only signal still on the element at that point.
export const selectTriggerClasses =
  "flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-surface-200 bg-surface-0 px-3 py-2 text-sm shadow-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 data-[state=open]:border-brand-500 data-[state=open]:ring-1 data-[state=open]:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1";
// The open list portals to <body>, so it must out-rank anything that can open it:
// the Radix Dialog (z-100) and the older hand-rolled modals (up to z-1100). Toasts
// (z-10000) stay on top.
export const selectPanelClasses =
  "z-[2000] overflow-hidden rounded-md border border-surface-200 bg-surface-0 text-surface-700 shadow-md";
export const selectItemClasses =
  "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none";

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(selectTriggerClasses, className)}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 shrink-0 text-surface-400" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => {
  // Radix focuses the highlighted item, so the shared pill glides with both
  // pointer and keyboard.
  const highlight = useMenuHighlight<HTMLDivElement>();
  return (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        // Capped by the room Radix measures on whichever side it opens, so a menu
        // near an edge shrinks and scrolls instead of running under the header.
        "relative max-h-[min(24rem,var(--radix-select-content-available-height))] min-w-[8rem]",
        selectPanelClasses,
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      // Prefer opening downwards, but allow Radix to flip when there is genuinely
      // no room -- a trigger near the bottom of the page otherwise gets an
      // available-height of ~0 and the panel collapses to a sliver showing only
      // the scroll-down chevron. Radix only flips when the content does not fit,
      // and the max-height above still caps a long list rather than letting it
      // run off-screen, so a long list does not flip merely for being long.
      side="bottom"
      // The top inset keeps a flipped-up menu clear of the app's top bar.
      collisionPadding={{ top: 88, right: 8, bottom: 8, left: 8 }}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        {...highlight.props}
        className={cn(
          // min-h-0 lets this flex child shrink below its content so `overflow: auto`
          // (set inline by Radix) actually scrolls rather than stretching the content.
          "relative isolate min-h-0 p-1",
          // Deliberately no height here. shadcn's stock class is
          // h-[var(--radix-select-trigger-height)], which Radix resolves to the
          // *trigger's* height — pinning the whole list to one row.
          position === "popper" && "w-full min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {highlight.indicator}
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label ref={ref} className={cn("px-2 py-1.5 text-xs font-semibold text-surface-500", className)} {...props} />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    {...{ [MENU_ITEM_ATTR]: "" }}
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-brand-50 focus:text-brand-700 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="menu-check h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={cn("-mx-1 my-1 h-px bg-surface-200", className)} {...props} />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
